import { defineStore } from "pinia";
import {
  InitConfigurator,
  getInitData,
  GetCriteria,
  SetCriteria,
  GetResults,
  getCodingImage,
  ChangeAssembly,
  resetAssembly,
  GetHelpTexts,
  SetParameter,
  GetExternalItemid,
  GetStructure,
  GetTechnicalEvaluation,
} from "@/api/obj.api";
import { useLoginStore } from "./LoginStore";
import { useAdditionalAttrStore } from "./AdditionalAttrStore";
import {
  ConfiguratorState,
  CurrentOption,
  InitDataType,
  Options,
  ProductImage,
  DisplayDataCriteriasItem,
  CurrentCodingCriteria,
  SelectedCertificateGroup,
  OriginatorsArr,
  ConfiguratorApiGetResults,
  AdminParams,
  StructureItem,
  ResponseTechnicalEvaluation,
} from "./types";
import {
  isCodingCertificate,
  isProductAssembly,
  isProductSelected,
} from "../utils/responseUtil";
import { isQuantityEnabled } from "@/hooks/summary";
import { useDataLayerStore } from "./DataLayerStore";
// import { configuratorIdStore } from "./configuratorIdStore";

export const useConfiguratorStore = defineStore("configuratorStore", {
  state: (): ConfiguratorState => ({
    apiContent: null,
    apiConfiguratorResult: null,
    RequireTechnicalEvaluation: null,
    apiImageContent: null,
    apiCodingImage: null,
    apiDisplayData: null,
    apiTechnicalEvaluation: null,
    typeCode: "",
    userSelectedOptions: [],
    userErroredSelectedOption: "",
    initialAssemblies: [],
    selectedAssemblies: [],
    initialCertificates: [],
    selectedCertificateGroupByAssembly: [],
    adminParams: {
      languageInput: "",
      regionInput: "",
      planInput: "",
    },
    saveAdminSettings: [],
    currentProductAssembly: false,
    currentProductOptionAssembly: false,
    currentProductOptionCertificate: false,
    resultData: null,
    infoText: [],
    deletedCertificates: [],
    buttonIsLoading: false,
    apiexternalitemid: null,
    lastSelectedCriteriaOption: null,
    lastSelectedcodeType: null,
    lastSelectedcodeTypeOpenWith: [],
    certificateSelectedUsing: null,
    certificateSelectorOpen: null,
    assemblySelectedUsing: null,
    assemblySelectorOpen: false,
    isGetResultsCalled: false,
    apiGetResults: null,
    allowOptionalModel: false,
    structure: [],
    codeHoverOn: "",
    codeisActive: "",
  }),
  getters: {
    getTextInfo: (state) => {
      return state.infoText;
    },
    getConfigureDataTypes: (state): InitDataType[] =>
      state.apiContent?.InitData?.Types ?? [],
    getSelectedConfigs: (state) => {
      return state.apiConfiguratorResult;
    },
    getConfiguratorResult: (state) => {
      if (state.apiConfiguratorResult) {
        return state.apiConfiguratorResult.ConfiguratorResult;
      }
    },
    getCurrentState: (state) => {
      if (
        state.apiConfiguratorResult &&
        state.apiConfiguratorResult.ConfiguratorResult
      ) {
        return state.apiConfiguratorResult.ConfiguratorResult.CurrentState;
      }
    },
    getCurrentOptions: (state): CurrentOption[] => {
      if (
        state.apiConfiguratorResult &&
        state.apiConfiguratorResult.ConfiguratorResult &&
        state.apiConfiguratorResult.ConfiguratorResult.CurrentState
      ) {
        let maxScore = 0;
        const currentOptions: CurrentOption[] =
          state.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
            ?.CurrentOptions;

        // calc max score for each items
        currentOptions.forEach((v) => {
          const deliverySpeedScore = parseInt(v.DeliverySpeedScore);
          maxScore =
            maxScore < deliverySpeedScore ? deliverySpeedScore : maxScore;
        });

        currentOptions.forEach((v) => {
          if (v.Selected == false && v.VariableValue !== null) {
            v.VariableValue = null;
          }

          v.maxScore = maxScore;
          if (maxScore == 5 && Number(v.DeliverySpeedScore) < 5) {
            v.DeliverySpeedScore = "0";
          }
        });

        return currentOptions;
      } else {
        return [];
      }
    },
    getImageState: (state) => {
      if (state.apiImageContent && state.apiImageContent.ImageData !== "") {
        return `data:image/png;base64, ${state.apiImageContent.ImageData}`;
      } else {
        return undefined;
      }
    },
    getSelectedProduct(state) {
      const currentCoding =
        state.apiConfiguratorResult?.ConfiguratorResult.CurrentState
          .CurrentCoding ?? [];
      return currentCoding.find(isProductSelected);
    },
    getIndexZProduct(state) {
      const currentCoding =
        state.apiConfiguratorResult?.ConfiguratorResult.CurrentState
          .CurrentCoding ?? [];
      return currentCoding[0];
    },
    getSelectedAssembly(state) {
      const currentCoding =
        state.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
          ?.CurrentCoding ?? [];
      const selectedProduct = currentCoding.find(isProductSelected);
      let selectedAssembly = selectedProduct;
      if (selectedProduct?.Parent) {
        selectedAssembly = currentCoding.find(
          (currentCoding) => currentCoding.CodeTypeId === selectedProduct.Parent
        );
      }
      return selectedAssembly;
    },
    getSelectedCertificates(state) {
      const certs: Array<string> = [];
      state.selectedCertificateGroupByAssembly.map((certificate) => {
        certificate.items.map((cert) => {
          certs.push(cert.codeTypeId);
        });
      });
      return certs;
    },
    getImageByCodeTypeId(state) {
      return (codeTypeId: string) =>
        state.thumbnails?.products.find((product) =>
          product.productModelCode.includes(codeTypeId)
        )?.productImages[0];
    },
    getSelectedProductImage(): ProductImage | undefined {
      const selectedProduct = this.getSelectedProduct;

      const codeTypeId = selectedProduct?.CodeTypeId;
      if (codeTypeId) {
        if (this.getImageByCodeTypeId(codeTypeId)) {
          return this.getImageByCodeTypeId(codeTypeId);
        } else if (selectedProduct.Parent) {
          return this.getImageByCodeTypeId(selectedProduct.Parent);
        }
      }
      return undefined;
    },
    getSelectedAssemblyImage(): ProductImage | undefined {
      const codeTypeId = this.getSelectedAssembly?.CodeTypeId;
      if (codeTypeId) {
        return this.getImageByCodeTypeId(codeTypeId);
      }
      return undefined;
    },
    getCodingImageState: (state) => {
      if (
        state.apiConfiguratorResult &&
        state.apiConfiguratorResult.ConfiguratorResult.CurrentState
          ?.CurrentImage.ImageData
      ) {
        return `data:image/png;base64, ${state.apiConfiguratorResult.ConfiguratorResult.CurrentState.CurrentImage.ImageData}`;
      } else {
        return undefined;
      }
    },
    getImageStyleState: (state) => {
      if (state.apiImageContent) {
        return `width: ${state.apiImageContent.DimensionWidth}px; height: ${state.apiImageContent.DimensionHeight}px`;
      } else {
        return "width:90px;height:90px;";
      }
    },
    getIsImageSet: (state) => state.apiImageContent?.HasImage == "true",
    getIsCodingImageSet: (state) =>
      state.apiCodingImage?.ImageInformation?.ImageData !== "",
    getUpdateRequired: (state) =>
      !!state.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
        ?.CurrentImage?.RequireUpdate &&
      state.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
        ?.CurrentImage?.HasImage,
    getTypeCode: (state) => {
      return state.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
        ?.CurrentCoding[0].ContentStringPublic;
    },
    getCurrentType: (state) => {
      return state.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
        ?.CurrentType;
    },
    getUserSelectedOptions: (state) => {
      if (state.userSelectedOptions) {
        return state.userSelectedOptions;
      } else {
        return [];
      }
    },
    getErroredSelectedOption: (state) => {
      if (state.userErroredSelectedOption) {
        return state.userErroredSelectedOption;
      } else {
        return "0";
      }
    },
    getLanguages: (state) => {
      return state.apiContent?.InitData?.Languages;
    },
    getRegion: (state) => {
      return state.apiContent?.InitData?.Regions;
    },
    getPlannumbers: (state) => {
      return state.apiContent?.InitData?.Plannumbers;
    },
    getResultData: (state: ConfiguratorState) => {
      return state.resultData;
    },
    getCurrentCodingCodes: (state: ConfiguratorState) => {
      return (
        state.apiConfiguratorResult?.ConfiguratorResult.CurrentState.CurrentCoding.map(
          (item) => item.CodeTypeId
        ) ?? []
      );
    },
    getSelectedCertificateGroup(
      state: ConfiguratorState
    ): SelectedCertificateGroup {
      const assemblyCode = this.getSelectedAssembly?.CodeTypeId;

      let selectedCertificateGroup =
        state.selectedCertificateGroupByAssembly.find(
          (certificate) => certificate.codeTypeId === assemblyCode
        );
      if (!selectedCertificateGroup) {
        selectedCertificateGroup = {
          codeTypeId: assemblyCode ?? "",
          items: [],
        };
        state.selectedCertificateGroupByAssembly.push(selectedCertificateGroup);
      }
      return selectedCertificateGroup;
    },
    getQuantity(state: ConfiguratorState): number | undefined {
      const result = state.resultData;
      return result?.Quantity ? parseInt(result.Quantity) : undefined;
    },
    getLastselectedCriteriaOption(
      state: ConfiguratorState
    ): Options | OriginatorsArr | null {
      return state.lastSelectedCriteriaOption;
    },
    getlastSelectedcodeType(state: ConfiguratorState): string | null {
      return state.lastSelectedcodeType;
    },
    getassemblySelectorOpen(state: ConfiguratorState): boolean {
      return state.assemblySelectorOpen;
    },
    getcertificateSelectorOpen(state: ConfiguratorState): boolean | null {
      return state.certificateSelectorOpen;
    },
    getlastSelectedcodeTypeOpenWith(state: ConfiguratorState): string[] {
      return state.lastSelectedcodeTypeOpenWith;
    },
    getisGetResultsCalled(state: ConfiguratorState): boolean {
      return state.isGetResultsCalled;
    },
    getRequireTechnicalEvaluation(state: ConfiguratorState): boolean {
      return state.RequireTechnicalEvaluation ?? false;
    },
  },
  actions: {
    setSelectedCertificateGroup(assemblyCode: string) {
      let selectedCertificateGroup =
        this.selectedCertificateGroupByAssembly.find(
          (certificate) => certificate.codeTypeId === assemblyCode
        );
      if (!selectedCertificateGroup) {
        selectedCertificateGroup = {
          codeTypeId: assemblyCode ?? "",
          items: [],
        };
        this.selectedCertificateGroupByAssembly.push(selectedCertificateGroup);
      }
    },
    getCriteriasByType(type: string): CurrentCodingCriteria[] {
      if (typeof type === "undefined") {
        type = "O";
      }
      if (this.apiConfiguratorResult) {
        const mandatoryItems =
          this.apiDisplayData?.Criterias.filter(
            (item: DisplayDataCriteriasItem) => item.DisplayType === type
          ) || [];
        const tempIds: string[] = [];
        mandatoryItems.forEach((v: DisplayDataCriteriasItem) => {
          tempIds.push(v.CriteriaIdx);
        });
        const product = this.getSelectedProduct;
        return (
          product?.Criterias.filter(
            (criteria: CurrentCodingCriteria) =>
              tempIds.indexOf(criteria.CriteriaIdx) > -1
          ) ?? []
        );
      }
      return [];
    },
    async initilizeConfigurator() {
      const store = useLoginStore();
      if (store.getLoginStatus) {
        const response = await getInitData();
        this.apiContent = response;
        this.userSelectedOptions = [];
        return response;
      } else {
        await store.LoginToStore();
        const response = await getInitData();
        this.apiContent = response;
        this.userSelectedOptions = [];
        return response;
      }
    },
    setSelectedOptions() {
      const selectProduct = this.getSelectedProduct;
      selectProduct?.Criterias.forEach((criterias: CurrentCodingCriteria) => {
        if (
          criterias.NettoOption !== "" &&
          criterias.OptionDescription !== ""
        ) {
          this.userSelectedOptions.push(criterias.CriteriaIdx);
        }
      });
    },
    async setSelectedConfig() {
      const result = await InitConfigurator();
      if (result.Status == "ERROR") {
        return result;
      }

      this.apiConfiguratorResult = result;
      this.apiDisplayData = result?.ConfiguratorResult.DisplayData;
      this.RequireTechnicalEvaluation =
        result?.ConfiguratorResult.ModelOptions.RequireTechnicalEvaluation;
      this.initialAssemblies =
        result?.ConfiguratorResult.CurrentState.CurrentCoding.filter(
          isProductAssembly
        ).map((item) => item.CodeTypeId) ?? [];
      this.initialCertificates =
        result?.ConfiguratorResult.CurrentState.CurrentCoding.filter(
          isCodingCertificate
        ).map((item) => item.CodeTypeId) ?? [];
      this.selectedAssemblies = [];
      this.selectedCertificateGroupByAssembly = [];
      result?.ConfiguratorResult.CurrentState.CurrentCoding.forEach(
        async (cc) => {
          const resInfo = await GetHelpTexts(cc.CodeTypeId);
          this.infoText.push({
            id: cc.CodeTypeId,
            items: resInfo.Criterias,
          });
        }
      );
      const additionalAttrStore = useAdditionalAttrStore();
      if (additionalAttrStore.getDataConfig.qty > 1) {
        const initQty = additionalAttrStore.getDataConfig.qty;
        await SetParameter("quantity", initQty);
      }
      this.setSelectedOptions();
      this.apiImageContent = result?.ConfiguratorResult.CurrentImage;
      if (additionalAttrStore.getDataConfig.autoUpdateDrawings === false) {
        this.apiCodingImage = await getCodingImage();
      } else {
        this.apiCodingImage = {
          ImageInformation:
            result?.ConfiguratorResult?.CurrentState.CurrentImage,
        };
      }
      await this.setStructure();

      await useDataLayerStore().dataLayerConfiguratorStart();
      useDataLayerStore().datalayerViewItem();
      return result;
    },
    async setStructure() {
      const response = await GetStructure();
      this.structure = response.Structure;
    },
    setStructureValue(Structure: Array<StructureItem>) {
      this.structure = Structure;
    },
    setCodeHoverOn: function (id: string) {
      this.codeHoverOn = id;
    },
    async GetApiCriteria(id: string, codeType: string) {
      const criteria = await GetCriteria(id, codeType);

      if (criteria.CurrentState.CurrentOptions.length == 0) {
        const userSelected = this.userSelectedOptions;
        userSelected.push(id);
        this.userSelectedOptions = userSelected;
      }

      if (this.apiConfiguratorResult) {
        this.apiConfiguratorResult.ConfiguratorResult.CurrentState =
          criteria.CurrentState;
        this.setSelectedOptions();
      }
      return criteria;
    },
    trimAssemblies() {
      const assemblies = this.selectedAssemblies ?? [];
      const availableCodes = this.getCurrentCodingCodes ?? [];

      const filteredAssemblies = assemblies.filter((assembly) =>
        availableCodes.includes(assembly.codeTypeId)
      );
      if (assemblies.length !== filteredAssemblies.length) {
        this.selectedAssemblies = filteredAssemblies;
      }

      const initialAssemblies = this.initialAssemblies ?? [];
      const filteredInitialAssemblies = initialAssemblies.filter((assembly) =>
        availableCodes.includes(assembly)
      );
      if (initialAssemblies.length !== filteredInitialAssemblies.length) {
        this.initialAssemblies = filteredInitialAssemblies;
      }
    },
    async getCodestatus() {
      return this.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
        .CodeComplete
        ? 1
        : 0;
    },
    async SetApiCriteria(options: Options, codeType: string) {
      this.buttonIsLoading = true;

      const selectedOption = options.CriteriaIdx + options.OptionId;

      if (this.certificateSelectedUsing == selectedOption) {
        this.certificateSelectorOpen = true;
      }

      if (this.assemblySelectedUsing == selectedOption) {
        this.assemblySelectorOpen = true;
      }

      const criteria = await SetCriteria(options, codeType);
      if (criteria.Status == "ERROR") {
        return criteria.ErrorText;
      }
      if (this.apiConfiguratorResult) {
        const additionalAttrStore = useAdditionalAttrStore();
        if (this.apiConfiguratorResult.Status == "OK") {
          if (this.userErroredSelectedOption) {
            const userSelected = this.userSelectedOptions;
            userSelected.push(options.CriteriaIdx);
            this.userSelectedOptions = userSelected;
            this.userErroredSelectedOption = "";
          }

          const userSelected = this.userSelectedOptions;
          userSelected.push(options.CriteriaIdx);
          this.userSelectedOptions = userSelected;
          if (this.getUpdateRequired) {
            if (
              additionalAttrStore.getDataConfig.autoUpdateDrawings === false
            ) {
              this.apiCodingImage = await getCodingImage();
            } else {
              this.apiCodingImage = {
                ImageInformation:
                  this.apiConfiguratorResult?.ConfiguratorResult?.CurrentState
                    ?.CurrentImage,
              };
            }
          }

          this.apiConfiguratorResult.ConfiguratorResult = criteria;

          if (
            this.apiConfiguratorResult.ConfiguratorResult.CurrentState
              .CodeComplete
          ) {
            useConfiguratorStore().setIsGetResultsCalled(true);
            await this.setResults();
          }
        }
        this.setSelectedOptions();
        const selectProduct = this.getSelectedProduct;
        if (selectProduct) {
          additionalAttrStore.setCurrentModelCodeObj(selectProduct);
        }
        this.trimAssemblies();

        const currentCerOptions =
          this.apiConfiguratorResult?.ConfiguratorResult.CurrentState.CurrentCoding.filter(
            (item) =>
              item.CodeTypeId != "LABEL" &&
              item.Optional === "Yes" &&
              this.allowOptionalModel == true &&
              !item?.Originators?.some(
                (org) =>
                  org.CodeTypeId !== this.lastSelectedcodeType ||
                  org.CriteriaId !==
                    this.getLastselectedCriteriaOption?.CriteriaId ||
                  org.OptionId !== this.getLastselectedCriteriaOption?.OptionId
              )
          ) ?? [];
        if (currentCerOptions.length > 0) {
          this.certificateSelectorOpen = true;
        }
      }
      this.buttonIsLoading = false;
      return criteria;
    },
    checkAssemblyProduct() {
      const selectProduct = this.getSelectedProduct;
      if (selectProduct && !selectProduct.Parent) {
        return selectProduct;
      }
      return false;
    },
    async ChangeAssembly(CodeTypeId: string) {
      const criteria = await ChangeAssembly(CodeTypeId);
      if (this.apiConfiguratorResult) {
        this.apiConfiguratorResult.ConfiguratorResult =
          criteria.ConfiguratorResult;
        this.apiDisplayData = criteria?.ConfiguratorResult.DisplayData;
        this.setuserSelectedOptions([]);
        this.setSelectedOptions();
      }
      return criteria;
    },
    async resetAssembly(CodeTypeId: string) {
      const response = await resetAssembly(CodeTypeId);
      if (this.apiConfiguratorResult && response.CurrentState) {
        this.apiConfiguratorResult.ConfiguratorResult.CurrentState =
          response.CurrentState;
      }
      return response;
    },
    setAdminParams(state: AdminParams) {
      this.adminParams = state;
    },
    async setUserErroredSelectedOption(id: string) {
      this.userErroredSelectedOption = id;
    },
    async setApiTechnicalEvaluation(
      apiTechnicalEvaluation: ResponseTechnicalEvaluation | null
    ) {
      this.apiTechnicalEvaluation = apiTechnicalEvaluation;
    },
    async setResults() {
      if(useConfiguratorStore()?.getRequireTechnicalEvaluation){
      const TechnicalEvalution = await GetTechnicalEvaluation();
      this.apiTechnicalEvaluation = TechnicalEvalution;
      }
      const result = await GetResults();
      useConfiguratorStore().setApiGetResults(result);
      this.resultData = result;
      const additionalAttrStore = useAdditionalAttrStore();
      if (additionalAttrStore.getCommunicationConfig.externalItemNoRequestUrl) {
        this.apiexternalitemid = await GetExternalItemid(
          result?.ItemInformation?.Item?.ItemId,
          additionalAttrStore.getCommunicationConfig.externalItemNoRequestUrl
        );
      }
      return this.resultData;
    },
    addSelectedAssembly(codeTypeId: string) {
      if (!this.selectedAssemblies) {
        this.selectedAssemblies = [];
      }
      const optional =
        this.getCurrentState?.CurrentCoding.find(
          (currentCoding) => currentCoding.CodeTypeId === codeTypeId
        )?.Optional === "Yes";
      this.selectedAssemblies.push({ codeTypeId, optional });
    },
    removeSelectedAssembly(codeTypeId: string) {
      if (!this.selectedAssemblies) {
        this.selectedAssemblies = [];
      }
      this.selectedAssemblies = this.selectedAssemblies.filter(
        (item) => item.codeTypeId !== codeTypeId
      );
      if (!this.initialAssemblies) {
        this.initialAssemblies = [];
      }
      this.initialAssemblies = this.initialAssemblies.filter(
        (item) => item !== codeTypeId
      );
    },
    addSelectedCertificate(codeTypeId: string) {
      const optional =
        this.getCurrentState?.CurrentCoding.find(
          (currentCoding) => currentCoding.CodeTypeId === codeTypeId
        )?.Optional === "Yes";

      const selectedCertificate = this.getSelectedCertificateGroup;
      selectedCertificate.items.push({ codeTypeId, optional });
    },
    removeSelectedCertificate(codeTypeId: string) {
      const selectedCertificate = this.getSelectedCertificateGroup;
      selectedCertificate.items = selectedCertificate.items.filter(
        (item) => item.codeTypeId !== codeTypeId
      );
    },
    deleteCertificate(cert: string) {
      this.deletedCertificates.push(cert);
    },
    setbuttonIsLoading(isLoading: boolean) {
      this.buttonIsLoading = isLoading;
    },
    setuserSelectedOptions(userSelectedOptions: string[]) {
      this.userSelectedOptions = userSelectedOptions;
    },
    async setQuantity(value: number) {
      this.buttonIsLoading = true;
      await SetParameter("quantity", value);
      if (isQuantityEnabled.value) {
        await this.setResults();
      }
      this.buttonIsLoading = false;
    },
    setlastSelectedOption(option: OriginatorsArr | Options) {
      this.lastSelectedCriteriaOption = option;
    },
    setlastSelectedcodeType(codeType: string) {
      this.lastSelectedcodeType = codeType;
    },
    setCertificateSelectedUsing(idx: string | null) {
      this.certificateSelectedUsing = idx;
    },
    setcertificateSelectorOpen(isOpen: boolean | null) {
      this.certificateSelectorOpen = isOpen;
    },
    setAssemblySelectorOpen(isOpen: boolean) {
      this.assemblySelectorOpen = isOpen;
    },
    setassemblySelectedUsing(idx: string | null) {
      this.assemblySelectedUsing = idx;
    },
    setlastSelectedcodeTypeOpenWith(openWith: string) {
      this.lastSelectedcodeTypeOpenWith.push(openWith);
    },
    setIsGetResultsCalled(isGetResultsCalled: boolean) {
      this.isGetResultsCalled = isGetResultsCalled;
    },
    setApiGetResults(apiGetResults: ConfiguratorApiGetResults | null) {
      if (this.apiConfiguratorResult) {
        if (apiGetResults?.ItemInformation.Description.ShortText) {
          this.apiConfiguratorResult.ConfiguratorResult.CurrentState.CurrentDescription =
            apiGetResults?.ItemInformation.Description.ShortText;
        }
        if (apiGetResults?.ItemInformation.Description.LongText) {
          this.apiConfiguratorResult.ConfiguratorResult.CurrentState.CurrentDescriptionLong =
            apiGetResults?.ItemInformation.Description.LongText;
        }
      }
      this.apiGetResults = apiGetResults;
    },
    setAllowallowOptionalModels(allowOptionalModel: boolean) {
      this.allowOptionalModel = allowOptionalModel;
    },
  },
});
